import { useCallback, useMemo } from 'react';

import { UIMode, UIModeProps } from 'framework-listing-lib/types';
import { UI_MODE } from 'framework-listing-lib/constants/uiMode';
import { useAppSettingsUIModes } from 'framework-listing-lib/internal/frameworkAppSettings/hooks/useFrameworkAppSettings';
import { useUiModeProps } from 'framework-listing-lib/internal/providers/ListingProvider';

export default function useUIMode(): UIModeProps {
  'use memo';
  const uiModes = useUIModes();
  const uiModeProps = useUiModeProps();

  return useMemo(() => {
    if (!uiModeProps) {
      return {
        currentUIMode: UI_MODE.LIST,
      };
    }

    /**
     * Pull the current UI mode from props and at least verify it
     * against the known UI modes. We can eventually confirm it is in
     * the configured UI modes. Defaulting to LIST can lead to filter
     * churn in folder mode.
     */
    const currentUIModeFromProps = uiModeProps.currentUIMode
      ? UI_MODE[uiModeProps.currentUIMode]
      : undefined;

    const currentUIMode =
      currentUIModeFromProps && uiModes.includes(currentUIModeFromProps)
        ? currentUIModeFromProps
        : currentUIModeFromProps || UI_MODE.LIST;

    return {
      currentUIMode,
      onChangeUIMode: uiModeProps.onChangeUIMode,
    };
  }, [uiModeProps, uiModes]);
}

export function useOnChangeUIMode() {
  'use memo';
  const currentUIMode = useCurrentUIMode();
  const uiModeProps = useUIMode();
  const uiModes = useUIModes();

  return useCallback(
    (newUIMode: UIMode) => {
      if (!uiModeProps.onChangeUIMode) {
        return;
      }

      if (currentUIMode === newUIMode) {
        return;
      }

      uiModeProps.onChangeUIMode(newUIMode, {
        previousUIMode: currentUIMode,
        uiModes,
      });
    },
    [currentUIMode, uiModeProps, uiModes]
  );
}

export function useUIModes() {
  'use memo';
  return useAppSettingsUIModes();
}

export function useCurrentUIMode() {
  'use memo';
  const uiModeProps = useUIMode();

  return useMemo(() => uiModeProps.currentUIMode, [uiModeProps.currentUIMode]);
}

export function useIsFoldersMode() {
  'use memo';
  const currentUIMode = useCurrentUIMode();

  return useMemo(() => currentUIMode === UI_MODE.FOLDERS, [currentUIMode]);
}

export function useIsGalleryMode() {
  'use memo';
  const currentUIMode = useCurrentUIMode();

  return useMemo(() => currentUIMode === UI_MODE.GALLERY, [currentUIMode]);
}

export function useIsCalendarMode() {
  'use memo';
  const currentUIMode = useCurrentUIMode();

  return useMemo(() => currentUIMode === UI_MODE.CALENDAR, [currentUIMode]);
}

export function useIsListMode() {
  'use memo';
  const currentUIMode = useCurrentUIMode();

  return useMemo(() => currentUIMode === UI_MODE.LIST, [currentUIMode]);
}

export function useHasFoldersMode() {
  'use memo';
  const uiModes = useUIModes();

  return useMemo(() => uiModes.includes(UI_MODE.FOLDERS), [uiModes]);
}
